"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const leaders = [{
  id: 1,
  name: "Alice Johnson",
  title: "Chief Executive Officer",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1497483/img/alice_johnson_ceo.png",
  bio: "Over 20 years of leadership in mining and resource development with a focus on ESG integration. Alice has driven operational excellence across multiple continents and established united mining power as a credible partner in the global EV battery supply chain."
}, {
  id: 2,
  name: "Michael Lee",
  title: "Chief Operating Officer",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1497483/img/michael_lee_coo.png",
  bio: "Extensive experience managing mining operations in Africa and implementing safety-first protocols. Michael's commitment to operational transparency and community engagement has been instrumental in achieving key project milestones."
}, {
  id: 3,
  name: "Sarah Martinez",
  title: "Chief Financial Officer",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1497483/img/sarah_martinez_cfo.png",
  bio: "Expert in project finance and capital markets with a proven track record in securing funding for large-scale mining projects. Sarah ensures fiscal discipline and investor confidence through transparent governance and strategic financial planning."
}];
export default function LeadershipTeam() {
  const [openStates, setOpenStates] = useState<Record<number, boolean>>({});
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const toggleOpen = (id: number) => {
    setOpenStates(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  return <section ref={sectionRef} className="bg-light-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className={`mb-12 text-center transition-all duration-700 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-display text-foreground-light text-3xl font-semibold sm:text-4xl">
            Leadership Team
          </h2>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-base">
            Experienced professionals driving operational excellence, ESG
            commitment, and investor confidence
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {leaders.map((leader, i) => <Collapsible key={leader.id} open={openStates[leader.id] || false} onOpenChange={() => toggleOpen(leader.id)} data-index={i}>
              <Card className={`group border-border bg-background transition-all duration-500 ${openStates[leader.id] ? "shadow-medium" : "shadow-soft hover:shadow-medium"} ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`} style={{
            transitionDelay: isVisible ? `${i * 100}ms` : "0ms"
          }}>
                <CardContent className="flex flex-col items-center pt-6 text-center">
                  <div className="mb-6 transition-transform duration-300 ease-out-back group-hover:-translate-y-1 group-hover:shadow-medium">
                    <SafeHydrate><Avatar className="size-32 border-4 border-muted shadow-soft">
                      <AvatarImage src={leader.image} alt={leader.name} className="object-cover" />
                      <AvatarFallback className="bg-primary text-foreground-primary text-3xl font-semibold">
                        {leader.name.split(" ").map(n => n[0]).join("")}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                  </div>

                  <h3 className="font-display text-foreground text-xl font-semibold">
                    {leader.name}
                  </h3>
                  <p className="text-muted-foreground mb-4 mt-1 text-sm">
                    {leader.title}
                  </p>

                  <CollapsibleTrigger className="text-primary hover:text-primary-hover focus-ring mb-4 flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors duration-150" aria-label={openStates[leader.id] ? `Collapse ${leader.name} bio` : `Expand ${leader.name} bio`}>
                    {openStates[leader.id] ? <>
                        <X className="size-4" />
                        <span>Close</span>
                      </> : <>
                        <Plus className="size-4" />
                        <span>Read Bio</span>
                      </>}
                  </CollapsibleTrigger>

                  <CollapsibleContent className="w-full overflow-hidden transition-all duration-300 data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                    <div className="border-border bg-muted-light mt-2 rounded-md border p-4 text-left">
                      <p className="text-muted-dark text-sm leading-relaxed">
                        {leader.bio}
                      </p>
                    </div>
                  </CollapsibleContent>
                </CardContent>
              </Card>
            </Collapsible>)}
        </div>
      </div>
    </section>;
}